import {
    NinchatReturnIcon,
    SelectorIcon,
} from '../Icon'

import {
    childTags,
    footer, footerIcon, sendTagsWrapper,
    tag as tagStyles,
    tags as tagsStyles,
} from './styles'

import Button from '../Button'
import React from 'react'
import {SendTagsViewProps} from './types'
import Tag from '../Tag'
import {
    TagProps,
} from '../Tag/types'

import {styled} from 'stitches/config'
import {useRovingTabIndex} from '../../utils/RovingTabIndex'
import {useTranslation} from 'react-i18next'

interface Tags {
    [key: string]: TagProps;
}

interface TagsStateProps {
    individualTags: Tags;
    parentTags: Tags;
}

const StyledSendTagsWrapper = styled('div', sendTagsWrapper)

const StyledFooterIcon = styled('span', footerIcon)
const StyledFooter = styled('div', footer)

const StyledTags = styled('div', tagsStyles)
const StyledIndividualTags = styled('div', {display: 'flex'})
const StyledTag = styled('div', tagStyles)
const StyledChildTags = styled('div', childTags)


const SendTagsView: React.FC<SendTagsViewProps> = (props: SendTagsViewProps): React.ReactElement => {
    const {
        disabled,
        isVideoChannel,
        onClose,
        onToggleTag,
        tags,
        tagIds,
    } = props

    const {t} = useTranslation()

    const defaultTagsState: TagsStateProps = {
        individualTags: {},
        parentTags: {},
    }

    const [tagsState, setTagsState] = React.useState(defaultTagsState)

    const listRef = useRovingTabIndex({
        filter: false,
        nodes: tagIds,
    })

    React.useEffect(() => {
        const individualTags: Tags = {}

        Object.keys(tags).map((tag_id) => {
            if (!tags[tag_id].tag_children || Object.keys(tags[tag_id].tag_children).length === 0) {
                const childTag = tags[tag_id] as TagProps

                childTag.selected = tagIds?.indexOf(tag_id) !== -1

                individualTags[tag_id] = childTag
            }

            return null
        })

        const parentTags: Tags = {}

        Object.keys(tags).map((tag_id) => {
            if (tags[tag_id].tag_children && Object.keys(tags[tag_id].tag_children).length > 0) {
                const parentTag = tags[tag_id] as TagProps

                parentTag.selected = tagIds?.indexOf(tag_id) !== -1

                Object.keys(parentTag.tag_children).map((child_id) => {
                    const childTag = parentTag.tag_children[child_id] as TagProps

                    childTag.selected = tagIds?.indexOf(child_id) !== -1

                    if (parentTag.tag_attrs?.name) {
                        childTag.parentName = parentTag.tag_attrs.name
                    }

                    if (parentTag.tag_attrs?.theme) {
                        childTag.tag_attrs = {
                            ...childTag.tag_attrs,
                            theme: parentTag.tag_attrs.theme,
                        }
                    }

                    parentTag.tag_children[child_id] = childTag

                    return null
                })

                parentTags[tag_id] = parentTag
            }

            return null
        })

        const newTagsState: TagsStateProps = {
            individualTags,
            parentTags,
        }
        setTagsState(newTagsState)
    }, [tags, tagIds])

    React.useEffect(() => {
        const onKeyup = (e: KeyboardEvent): void => {
            switch (e.key) {
                case 'Escape':
                    onClose()
                    break
                default:
                    break
            }
        }

        window.addEventListener('keyup', onKeyup)

        return () => window.removeEventListener('keyup', onKeyup)
    }, [])

    const sort = (items: Array<[key: string, details: TagProps ]>) => items.sort((tag, tag2) => {
        const tag1Name = tag[1].tag_attrs?.name || ''
        const tag2Name = tag2[1].tag_attrs?.name || ''

        return tag1Name.localeCompare(tag2Name)
    })


    const renderTag = (tag_id: string, tag: TagProps, isChild?: boolean): React.ReactElement => (
        <StyledTag key={tag_id}>
            <button
                disabled={disabled}
                onClick={(): void => onToggleTag(tag_id)}
                tabIndex={-1}
                title={tag.selected ? t('Remove tag {{name}}', {name: tag.tag_attrs?.name}) : t('Add tag {{name}}', {name: tag.tag_attrs?.name})}
                type='button'
            >
                <Tag showTheme={true} {...tag} noLeftBorder={isChild} />
            </button>
        </StyledTag>
    )

    const renderTags = (tag_id: string, tag: TagProps) => {
        const children = tag.tag_children
        return (
            <div key={tag_id}>
                {renderTag(tag_id, tag)}
                <StyledChildTags>
                    {sort(Object.entries(children)).map(([child_id]) => <div key={child_id}>{renderTag(child_id, children[child_id], true)}</div>)}
                </StyledChildTags>
            </div>
        )
    }

    return (
        <StyledSendTagsWrapper ref={listRef}>
            <StyledTags oneColumn={isVideoChannel}>
                <div>
                    <StyledIndividualTags>
                        {sort(Object.entries(tagsState.individualTags)).map(([tag_id]) => renderTag(tag_id, tagsState.individualTags[tag_id]))}
                    </StyledIndividualTags>
                    {sort(Object.entries(tagsState.parentTags)).map(([tag_id]) => renderTags(tag_id, tagsState.parentTags[tag_id]))}
                </div>
            </StyledTags>
            <StyledFooter>
                <div>
                    <small><StyledFooterIcon><SelectorIcon /></StyledFooterIcon>{t('navigate')}</small>
                    <small><StyledFooterIcon><NinchatReturnIcon /></StyledFooterIcon>{t('select')}</small>
                    <small><span>esc</span> {t('exit')}</small>
                </div>
                <Button color='filled' onPress={(): void => onClose()} tabIndex={-1} text={t('Done')} title={t('Close tags')} />
            </StyledFooter>
        </StyledSendTagsWrapper>
    )
}

export default SendTagsView
